import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const PROCESS_IMAGES = [
  "https://readdy.ai/api/search-image?query=modern%20business%20consultation%20meeting%20scene%2C%20professional%20team%20discussing%20at%20sleek%20conference%20table%20with%20laptop%20and%20documents%2C%20warm%20natural%20lighting%20through%20large%20windows%2C%20minimalist%20office%20interior%2C%20navy%20blue%20and%20white%20color%20scheme%2C%20corporate%20photography%20style&width=600&height=400&seq=process01&orientation=landscape",
  "https://readdy.ai/api/search-image?query=business%20data%20analysis%20dashboard%20on%20multiple%20screens%2C%20charts%20and%20graphs%20showing%20market%20trends%2C%20professional%20analyst%20workspace%2C%20modern%20tech%20office%20environment%2C%20clean%20minimalist%20design%2C%20navy%20blue%20accent%20colors%2C%20high%20quality%20photography&width=600&height=400&seq=process02&orientation=landscape",
  "https://readdy.ai/api/search-image?query=business%20partnership%20agreement%20signing%2C%20professional%20contract%20signing%20ceremony%2C%20modern%20office%20meeting%20room%2C%20trust%20and%20collaboration%20atmosphere%2C%20clean%20corporate%20photography%2C%20navy%20blue%20suits%2C%20bright%20professional%20lighting&width=600&height=400&seq=process03&orientation=landscape",
  "https://readdy.ai/api/search-image?query=customer%20support%20team%20working%20in%20modern%20office%2C%20multiple%20monitors%20showing%20order%20management%20system%2C%20professional%20logistics%20operation%20center%2C%20organized%20warehouse%20management%2C%20clean%20bright%20workspace%2C%20navy%20blue%20and%20white%20corporate%20colors&width=600&height=400&seq=process04&orientation=landscape",
];

const STEP_ICONS = [
  "ri-mail-send-line",
  "ri-line-chart-line",
  "ri-hand-coin-line",
  "ri-customer-service-2-line",
];

const STEP_NUMBERS = ["01", "02", "03", "04"];
const TIMELINE_STEPS = ["t1", "t2", "t3", "t4", "t5"] as const;
const FAQ_ITEMS = [
  { q: "q1", a: "a1" },
  { q: "q2", a: "a2" },
  { q: "q3", a: "a3" },
  { q: "q4", a: "a4" },
] as const;

export default function ProcessPage() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.1 }
    );
    const children = document.querySelectorAll(".animate-on-scroll");
    children.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#1a2332] transition-colors mb-8"
            >
              <i className="ri-arrow-left-line"></i>
              <span>{t("services.backToHome")}</span>
            </Link>

            <p className="text-xs text-gray-400 tracking-[0.4em] uppercase mb-4">
              {t("process.label")}
            </p>
            <h1 className="text-4xl md:text-6xl font-black text-[#1a2332] leading-tight mb-4">
              {t("process.title")}
            </h1>
            <p className="text-gray-500 max-w-xl leading-relaxed">
              {t("process.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="space-y-24 md:space-y-32">
            {STEP_NUMBERS.map((num, index) => (
              <div
                key={num}
                className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                  <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100">
                      <img
                        src={PROCESS_IMAGES[index]}
                        alt={t(`process.steps.s${index + 1}Title`)}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                    <div className="flex items-start gap-6 mb-6">
                      <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[#1a2332] text-white flex-shrink-0">
                        <i className={`${STEP_ICONS[index]} text-2xl`}></i>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400 tracking-[0.2em] uppercase">
                          STEP {num}
                        </span>
                        <h3 className="text-2xl md:text-3xl font-bold text-[#1a2332] mt-1">
                          {t(`process.steps.s${index + 1}Title`)}
                        </h3>
                        <p className="text-sm text-gray-400 mt-0.5">
                          {t(`process.steps.s${index + 1}TitleEn`)}
                        </p>
                      </div>
                    </div>
                    <p className="text-gray-600 leading-relaxed text-base">
                      {t(`process.steps.s${index + 1}Desc`)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 md:py-24 bg-[#fafafa]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <div className="text-center mb-12">
              <p className="text-xs text-gray-400 tracking-[0.4em] uppercase mb-3">
                {t("process.timelineLabel")}
              </p>
              <h2 className="text-3xl md:text-4xl font-black text-[#1a2332]">
                {t("process.timelineTitle")}
              </h2>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gray-200 md:-translate-x-px"></div>

              <div className="space-y-8">
                {TIMELINE_STEPS.map((key, index) => (
                  <div
                    key={key}
                    className={`flex items-center gap-6 ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? "md:text-right" : "md:text-left"}`}>
                      <div className={`bg-white rounded-xl p-5 border border-gray-100 inline-block ${index % 2 === 0 ? "md:ml-auto" : "md:mr-auto"}`}>
                        <span className="text-xs font-semibold text-[#1a2332] bg-[#1a2332]/5 px-3 py-1 rounded-full">
                          STEP{index + 1}
                        </span>
                        <p className="text-gray-700 mt-2 text-sm">{t(`process.timeline.${key}`)}</p>
                      </div>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-[#1a2332] border-4 border-white flex-shrink-0 relative z-10"></div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <div className="text-center mb-12">
              <p className="text-xs text-gray-400 tracking-[0.4em] uppercase mb-3">
                {t("process.faqLabel")}
              </p>
              <h2 className="text-3xl md:text-4xl font-black text-[#1a2332]">
                {t("process.faqTitle")}
              </h2>
            </div>

            <div className="space-y-4">
              {FAQ_ITEMS.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 border border-gray-100 hover:border-gray-200 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-[#1a2332]/5 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-[#1a2332]">Q</span>
                    </span>
                    <div>
                      <h4 className="font-semibold text-[#1a2332] mb-2">{t(`process.faq.${faq.q}`)}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{t(`process.faq.${faq.a}`)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#1a2332]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <p className="text-xs text-white/40 tracking-[0.4em] uppercase mb-5">
              CONTACT
            </p>
            <h2 className="text-3xl md:text-5xl font-black text-white leading-tight mb-6">
              {t("process.ctaTitle")}
            </h2>
            <p className="text-white/60 mb-10 max-w-lg mx-auto">
              {t("process.ctaSubtitle")}
            </p>
            <Link
              to="/"
              onClick={() => setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 100)}
              className="inline-flex items-center gap-3 bg-white text-[#1a2332] font-semibold text-sm px-8 py-4 rounded-full hover:bg-gray-100 transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              <span>{t("process.ctaButton")}</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
